import React from 'react';
import { getDefaultChainColor, getDefaultLigandColor, getDefaultPocketColor } from '../../contexts/ControlTypes';
import { render_chain_ids } from '../../helpers/TableHelper';
import {
   defaultLigandRepresentation,
   defaultPocketRepresentation,
   defaultRestRepresentation,
   Representation,
   StructureComponentType,
} from '../molstar/Controls/StructureRepresentationComponent';
import { AhojTableBase } from './AhojTableBase';
import ColorPairButton from './ColorPairButton';
import StructureRepresentationControlComponent from './StructureRepresentationControlComponent';

/**
 * Table showing the **query pocket** structure — typically a single row
 * corresponding to the user-submitted PDB structure with its binding-site
 * highlighted. Loaded immediately on page load (not toggled by the user).
 *
 * Because the query is always shown, the visibility toggle is disabled and the
 * row is pre-selected so the camera is focused on it at startup.
 */
class QuerySitesTable extends AhojTableBase {
   render(): React.ReactNode {
      const sortedData = this.getSortedData(this.props.data.query_pockets);
      const exp = this.expandedColumns;
      // DEFAULT: 10 cols — Structure | POI | Lig | pRMSD | SASA | 👁 | ✏ | Seq | ℹ | 🎨
      // EXPANDED: 13 cols — + Res | MBR | Vol
      const colSpan = exp ? 13 : 10;
      return (
         <div className="found-struct-box">
            {this.props.data.num_query_pockets > 0 && (
               <div className="found-struct-table">
                  <table id="query-sites">
                     <thead>
                        <tr>
                           <th data-tooltip="Matching structure (PDB_ID/chains)">Structure</th>
                           <th data-tooltip="Query Point Of Interest. Selection of ligand or other entity based on user query. Format: &lt;chain_id&gt;_&lt;residue_name&gt;_&lt;residue_index&gt; (author fields in mmCIF).">
                              POI
                           </th>
                           {exp && (
                              <>
                                 <th
                                    data-tooltip="Resolution of the PDB structure (the best resolution is stated in the presence of multiple). Click to sort."
                                    style={this.sortableThStyle}
                                    onClick={(): void => this.handleSort('resolution')}
                                 >
                                    Res{this.sortIndicator('resolution')}
                                 </th>
                                 <th
                                    data-tooltip="Mapped binding residues (observed). Number of mapped binding residues in UniProt and SEQRES that are observed in the structure (#resis3 in results). *MBR-SEQRES on hover. Click to sort."
                                    className="so"
                                    style={this.sortableThStyle}
                                    onClick={(): void => this.handleSort('mapped_binding_residues_observed')}
                                 >
                                    MBR{this.sortIndicator('mapped_binding_residues_observed')}
                                 </th>
                              </>
                           )}
                           <th data-tooltip="Ligands present in query pocket or mapped candidate pocket.">Lig</th>
                           <th
                              data-tooltip="Pocket RMSD: all-atom RMSD from the sequence-based pocket alignment (0 refinement cycles). Click to sort."
                              style={this.sortableThStyle}
                              onClick={(): void => this.handleSort('pocket_rmsd')}
                           >
                              pRMSD{this.sortIndicator('pocket_rmsd')}
                           </th>
                           <th
                              data-tooltip="Solvent Accessible Surface Area of pocket residues (UniProt SEQRES observed). Click to sort."
                              style={this.sortableThStyle}
                              onClick={(): void => this.handleSort('sasa')}
                           >
                              SASA{this.sortIndicator('sasa')}
                           </th>
                           {exp && <th data-tooltip="Pocket volume calculated by PyVOL (default settings).">Vol</th>}
                           <th data-tooltip="Show/hide first 20 structures">
                              <button
                                 className="btn-showhideall"
                                 style={{ width: '1em' }}
                                 onClick={(): void => this.showHideAll(sortedData)}
                              >
                                 <img src="/icons/eye.svg" alt="show/hide" width="100%" />
                              </button>
                           </th>
                           <th data-tooltip="Edit representation">&#9998;</th>
                           <th data-tooltip="Add/remove from Sequence Viewer">Seq</th>
                           <th
                              data-tooltip={exp ? 'Collapse to default columns' : 'Expand to show all columns'}
                              style={{ cursor: 'pointer' }}
                              className={exp ? 'active' : ''}
                              onClick={(): void => this.toggleExpandedColumns()}
                           >
                              ℹ
                           </th>
                           <th data-tooltip="Quick color — click a row to apply a unique color pair">
                              {this.hasAnyColorPairActive(sortedData) ? (
                                 <button title="Reset all colors" onClick={(e): void => this.resetAllColors(e)}>
                                    &#8635;
                                 </button>
                              ) : (
                                 <span style={{ opacity: 0.35, fontSize: '0.85em' }}>◉</span>
                              )}
                           </th>
                        </tr>
                     </thead>
                     <tbody>
                        <>
                           {sortedData.map((query_pocket) => (
                              <React.Fragment key={query_pocket.row_id}>
                                 <tr
                                    data-row-id={query_pocket.row_id}
                                    onClick={(): void => this.selectUnselectStructure(query_pocket)}
                                    className={`clickable-row ${this.context.controlsMap.get(query_pocket.row_id)?.isSelected ? 'selected-row' : ''}`}
                                 >
                                    <td>
                                       <a
                                          href={`https://www.rcsb.org/structure/${query_pocket.pdb_id}`}
                                          target="_blank"
                                          title="go to rcsb"
                                       >
                                          {query_pocket.pdb_id}
                                       </a>
                                       &nbsp;<span className="slash">/</span>&nbsp;
                                       {render_chain_ids(query_pocket.chains)}
                                    </td>
                                    <td className="so">{query_pocket.target_poi_alt.replace('_', ' ')}</td>
                                    {exp && (
                                       <>
                                          <td title={`Exp. method: ${query_pocket.experimental_method}`}>
                                             {query_pocket.resolution}
                                          </td>
                                          <td
                                             title={`MBR-SEQRES: ${query_pocket.mapped_binding_residues_num ?? 0} (${query_pocket.mapped_binding_residues_percent ?? 0}%)`}
                                          >
                                             {query_pocket.mapped_binding_residues_observed}
                                          </td>
                                       </>
                                    )}
                                    <td>{query_pocket.ligands ? query_pocket.ligands.join(' ') : '–'}</td>
                                    <td title={`aligned length: ${query_pocket.pocket_len}`}>
                                       {query_pocket.pocket_rmsd ?? '–'}
                                    </td>
                                    <td title={`avg. SASA: ${query_pocket.sasa_avg ?? 0}`}>
                                       {query_pocket.sasa ?? '–'}
                                    </td>
                                    {exp && <td>{query_pocket.volume ?? '–'}</td>}
                                    <td>
                                       <button
                                          className={`btn-showhide${this.context.controlsMap.get(query_pocket.row_id)?.isVisible ? '' : ' hidden'}`}
                                          style={{ width: '1em' }}
                                          title="show / hide"
                                          onClick={(e): void => {
                                             e.stopPropagation();
                                             this.showHideStructure(query_pocket);
                                          }}
                                       >
                                          <img src="/icons/eye.svg" alt="show/hide" width="100%" />
                                       </button>
                                    </td>
                                    <td>
                                       <button
                                          title="edit"
                                          onClick={(e): void => this.toggleEditPanel(query_pocket.row_id, e)}
                                          style={{ cursor: 'pointer' }}
                                       >
                                          {this.editingRowId === query_pocket.row_id ? '▼' : '▶'}
                                       </button>
                                    </td>
                                    <td>
                                       <button
                                          title={
                                             this.context.selectedPdb.some((s) => s.rowId === query_pocket.row_id)
                                                ? 'Remove from Sequence Viewer'
                                                : 'Add to Sequence Viewer'
                                          }
                                          className={`btn-seq${this.context.selectedPdb.some((s) => s.rowId === query_pocket.row_id) ? ' active' : ''}`}
                                          onClick={(e): void => {
                                             e.stopPropagation();
                                             this.addOrRemoveFromRcsb(query_pocket);
                                          }}
                                       >
                                          {this.context.selectedPdb.some((s) => s.rowId === query_pocket.row_id)
                                             ? '−'
                                             : '+'}
                                       </button>
                                    </td>
                                    <td>
                                       <button
                                          className={`btn-info${this.infoRowId === query_pocket.row_id ? ' active' : ''}`}
                                          title="Show info"
                                          onClick={(e): void => this.toggleInfoPanel(query_pocket.row_id, e)}
                                       >
                                          ℹ
                                       </button>
                                    </td>
                                    <td className="color-pair-cell">
                                       <ColorPairButton
                                          pocketColor={
                                             this.context.controlsMap.get(query_pocket.row_id)?.pocketColor ??
                                             getDefaultPocketColor('query_pocket')
                                          }
                                          chainColor={
                                             this.context.controlsMap.get(query_pocket.row_id)?.chainColor ??
                                             getDefaultChainColor('query_pocket')
                                          }
                                          active={this.isColorPairActive(query_pocket.row_id)}
                                          onClick={(e): void => {
                                             e.stopPropagation();
                                             this.selectNextColorPair(query_pocket);
                                          }}
                                          title="Apply unique color pair"
                                       />
                                    </td>
                                 </tr>
                                 {this.editingRowId === query_pocket.row_id && (
                                    <tr>
                                       <td colSpan={colSpan} className="edit-panel-cell">
                                          <div className="edit-panel-body">
                                             <div className="edit-panel-controls">
                                                <StructureRepresentationControlComponent
                                                   mode="big"
                                                   values={{
                                                      chain: {
                                                         representation:
                                                            this.context.controlsMap
                                                               .get(query_pocket.row_id)
                                                               ?.selectedRepresentations?.get('chain') ??
                                                            defaultRestRepresentation,
                                                         color:
                                                            this.context.controlsMap.get(query_pocket.row_id)
                                                               ?.chainColor ?? getDefaultChainColor('query_pocket'),
                                                      },
                                                      pocket: {
                                                         representation:
                                                            this.context.controlsMap
                                                               .get(query_pocket.row_id)
                                                               ?.selectedRepresentations?.get('pocket') ??
                                                            defaultPocketRepresentation,
                                                         color:
                                                            this.context.controlsMap.get(query_pocket.row_id)
                                                               ?.pocketColor ?? getDefaultPocketColor('query_pocket'),
                                                      },
                                                      ligand: {
                                                         representation:
                                                            this.context.controlsMap
                                                               .get(query_pocket.row_id)
                                                               ?.selectedRepresentations?.get('ligand') ??
                                                            defaultLigandRepresentation,
                                                         color:
                                                            this.context.controlsMap.get(query_pocket.row_id)
                                                               ?.ligandColor ?? getDefaultLigandColor(),
                                                      },
                                                   }}
                                                   onRepresentationChange={(
                                                      target: StructureComponentType,
                                                      type: Representation,
                                                   ): void => this.changeRepresentation(type, query_pocket, target)}
                                                   onColorChange={(
                                                      target: StructureComponentType,
                                                      color: string,
                                                   ): void => this.changeColor(query_pocket, color, target)}
                                                />
                                             </div>
                                          </div>
                                       </td>
                                    </tr>
                                 )}
                                 {this.infoRowId === query_pocket.row_id && (
                                    <tr>
                                       <td colSpan={colSpan} className="info-panel-cell">
                                          <div className="info-panel-body">
                                             <div className="info-panel-grid">
                                                <div className="info-panel-item">
                                                   <span
                                                      className="info-panel-label"
                                                      data-tooltip="Resolution of the PDB structure (the best resolution is stated in the presence of multiple)."
                                                   >
                                                      Res
                                                   </span>
                                                   <span
                                                      className="info-panel-value"
                                                      title={`Exp. method: ${query_pocket.experimental_method}`}
                                                   >
                                                      {query_pocket.resolution ?? '–'}
                                                   </span>
                                                </div>
                                                <div className="info-panel-item">
                                                   <span
                                                      className="info-panel-label"
                                                      data-tooltip="Mapped binding residues (observed). Number of mapped binding residues in UniProt and SEQRES that are observed in the structure."
                                                   >
                                                      MBR
                                                   </span>
                                                   <span
                                                      className="info-panel-value"
                                                      title={`MBR-SEQRES: ${query_pocket.mapped_binding_residues_num ?? 0} (${query_pocket.mapped_binding_residues_percent ?? 0}%)`}
                                                   >
                                                      {query_pocket.mapped_binding_residues_observed ?? '–'}
                                                   </span>
                                                </div>
                                                <div className="info-panel-item">
                                                   <span
                                                      className="info-panel-label"
                                                      data-tooltip="Pocket volume calculated by PyVOL (default settings)."
                                                   >
                                                      Vol
                                                   </span>
                                                   <span className="info-panel-value">
                                                      {query_pocket.volume ?? '–'}
                                                   </span>
                                                </div>
                                             </div>
                                          </div>
                                       </td>
                                    </tr>
                                 )}
                              </React.Fragment>
                           ))}
                        </>
                     </tbody>
                  </table>
               </div>
            )}
         </div>
      );
   }
}

export default QuerySitesTable;
